import React, { useEffect, useRef, useState } from "react";
import { fetchToken } from "../services/token";

type TableauVizModule = {
  TableauViz: new () => any;
  TableauEventType: Record<string, string>;
  Toolbar: Record<string, string>;
};

const TABLEAU_OPTIONS = {
  hideTabs: true,
  width: "100%",
  height: "1700px",
};

interface TableauVizProps {
  uuid: string;
  view: string | null;
  contentURL: string;
  switchView: (view: string) => void;
}

const TableauVizComponent: React.FC<TableauVizProps> = ({
  uuid,
  view,
  contentURL,
  switchView,
}) => {
  // component has mounted
  const hasMounted = useRef<boolean>(false);
  // the dashboard is initializing
  const isInitializing = useRef<boolean>(false);
  const [vizInstance, setVizInstance] = useState<any | null>(null);
  const baseUrl = import.meta.env.VITE_TABLEAU_URL;
  const embeddingApiVersion = import.meta.env.VITE_TABLEAU_EMBEDDING_API_VERSION;

  const initializeViz = async () => {
    try {
      if (isInitializing.current) return;

      if (!embeddingApiVersion) {
        throw new Error("Missing VITE_TABLEAU_EMBEDDING_API_VERSION");
      }

      isInitializing.current = true;
      if (vizInstance) {
        vizInstance.remove();
        setVizInstance(null);
      }

      // Fetch token for trusted authentication
      const token = await fetchToken();

      const tableauModule = (await import(
        /* @vite-ignore */ `${new URL(baseUrl).origin}/javascripts/api/tableau.embedding.${embeddingApiVersion}.min.js`
      )) as TableauVizModule;
      const { TableauViz, TableauEventType, Toolbar } = tableauModule;

      const viz = new TableauViz();
      // MINIMAL CHANGE #1: keep navigation inside the iframe (prevents app URL changes)
      const params = [
        ":embed=yes",
        ":showVizHome=no",
        ":linktarget=_self",
      ].join("&");
      viz.src = `${baseUrl}/trusted/${token}/views/${contentURL}?${params}`;

      viz.addEventListener(TableauEventType.FirstInteractive, () => {
        setVizInstance(viz);
        isInitializing.current = false;
      });

      viz.addEventListener(TableauEventType.TabSwitched, (event: any) => {
        try {
          // Get new sheet name from the event payload
          const newViewName = event?.detail?.newSheetName;

          // Sync React state + URL + tabs
          if (typeof newViewName === "string" && newViewName.length > 0) {
            switchView(newViewName);
          }
        } catch (err) {
          console.error("Error handling TabSwitched event:", err);
        }

        // Keep your existing scroll reset behavior
        const tableauElement = document.getElementById("tableauViz");
        if (tableauElement) {
          let parent = tableauElement.parentElement;
          while (parent) {
            if (parent.scrollTop > 0) {
              parent.scrollTop = 0;
            }
            parent = parent.parentElement;
          }
        }
      });

      viz.toolbar = Toolbar.Bottom;
      viz.hideTabs = TABLEAU_OPTIONS.hideTabs;
      viz.width = TABLEAU_OPTIONS.width;
      viz.height = TABLEAU_OPTIONS.height;

      const tableauVizElement = document.getElementById("tableauViz");

      if (tableauVizElement) {
        tableauVizElement.innerHTML = "";
        tableauVizElement.appendChild(viz);
      } else {
        console.error("Tableau Viz element not found");
      }
    } catch (error) {
      console.error("Error initializing Tableau Viz:", error);
    }
  };

  useEffect(() => {
    initializeViz();
  }, [uuid]);

  useEffect(() => {
    const switchTab = async () => {
      if (view) {
        if (vizInstance) {
          const workbook = vizInstance.workbook;
          const activeSheet = workbook.activeSheet;

          if (view !== activeSheet.name) {
            // Activating a sheet within the same workbook preserves filters by default
            await workbook
              .activateSheetAsync(view)
              .catch((err: unknown) =>
                console.error("Error switching view:", err),
              );
          }
        } else {
          // Not initialized yet: keep your existing behavior
          switchView(view);
          isInitializing.current = false;
          initializeViz();
        }
      }
    };

    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    switchTab();
  }, [view]);

  return <div id="tableauViz" className="bg-white pt-3" />;
};

export default TableauVizComponent;
